/* eslint-disable complexity */
import Timeline from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/brevity/timeline';
import Animation from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/brevity/animation';
import {
    getDifferentProperty,
    getMediaType,
    isMediaElement,
    isElementVisible,
    getFillColor,
    getFontColor,
    getLineColor,
    getFontSize,
    calculateHSLColor,
    getAnimTargetKeyOrderMap,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-utils';
import { getTargetComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-component-relate';
import {
    PLAY_MODE, ANIM_STATE,
    CHILD_TL, ANIM_PROPERTY,
    colorAnimTypeList,
} from '@src/js/js-keyframe-animation/style-converter/constants';
import {
    PATH_ANIM_TYPES,
    nodeTypes, presetClassTypes,
} from '@src/slide/domain/ui-model/element-modeles/property/animation/constant';
import { ANIM_CANVAS_PROPERTY, AnimFilters, TIME_CONSTANTS, Action, AnimCanvasProperty, ColorPropertyValues, ALL_COLOR_LIST, COLOR, ATTR_LIST } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-constants';
import {
    initFromStyle, initToStyle,
} from '@src/js/js-keyframe-animation/style-converter/convertAnimInfos';
import isUndefined from 'lodash/isUndefined';
import AnimMediaControlFactory from './anim-media-control/anim-media-control-factory';

// interface
import type AnimationModel from '@src/slide/domain/ui-model/element-modeles/anim-model';
import {
    IAnimCompleteParams,
    IAnimStartParams,
    IAnimList,
    ILabelParams,
    IAnimUpdateParams,
    IAnimTarget,
    ITimelineParams,
    IAnimFilterOptions,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-interface';
import { isGroupModel, isIframeShapeModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { IAnimModel, IAnimSimpleStyle, IFormatStyle, IToStyle } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import { ISlideComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/types';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import AnimFiltersManager from './anim-filters/anim-filters-manager';
import { IMediaManager } from '@src/slide/application/service/media-manager/media-manager.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { ElementsAnalyze } from '@src/slide/domain/ui-model/element-modeles/elements-analysis';
import { ILayerService } from '../../canvas-render/slide/layered-render/plalyer-state-layered/player-layer-interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import type { ComponentAnimWrapper } from '../../canvas-render/elements/component-anim-wrapper';
import { AnimType, PresetSubtype, IEGTimeNodeChoice } from '@tencent/slide-sdk-model';
import { normalizeRgba } from '@src/slide/infrastructure/module/independent/color/transform';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import i18n from '@i18next/i18n';

export default class BaseTimeline {
    public animList: IAnimModel[];
    public slideComponent?: ISlideComponent;
    public mediaManager: IMediaManager;
    public masterTimeline;
    public originalStyleMap;
    public lastStyleMap;
    public animFiltersManager: AnimFiltersManager;
    protected animMediaControl;
    protected tempStyleMap;
    protected differentPropertyMap;
    protected animTargetKeyOrderMap: Map<string, number[]>; // key => [order1,order2]
    protected options;
    private playManager;

    public constructor({ animList, slideComponent, mediaManager, playManager, options }: ITimelineParams) {
        this.animList = this.reorganizeAnimList(animList, slideComponent?.options.slideModel);
        this.slideComponent = slideComponent;
        this.mediaManager = mediaManager;
        this.playManager = playManager;
        this.options = options || { paused: false };
        this.originalStyleMap = new Map();
        this.lastStyleMap = new Map();
        this.tempStyleMap = new Map();
        this.differentPropertyMap = new Map();
        this.animFiltersManager = AnimFiltersManager.getInstance();
    }

    /**
     * 生成总时间轴，可以通过该总时间轴控制所有动画
     * @param anims
     * @returns Timeline
     */
    public getMasterTimeline(
        anims: Array<IAnimModel>,
        slideComponent: ISlideComponent,
        layerService?: ILayerService,
    ): Timeline {
        const animsOrderMap = this.getAnimsByOrder(anims);
        const timelineOptions = {
            id: 'total',
            paused: this.options?.masterPaused,
            onStart: this.handleMasterStart,
            onComplete: this.handleMasterComplete,
            onCompleteParams: [animsOrderMap],
            callbackScope: this,
        };
        const masterTimeline = new Timeline(timelineOptions);
        animsOrderMap.forEach((animList, order) => {
            const childTimeline = new Timeline({
                id: `order${order}`,
                paused: this.options?.childPaused,
                onStart: this.handleChildTlStart.bind(this),
                onUpdate: this.handleChildTlUpdate.bind(this),
                onComplete: this.handleChildTlComplete.bind(this),
                onStartParams: [animList],
                onUpdateParams: [animList],
                onCompleteParams: [order, animList, this.options?.autoPlay],
                callbackScope: this,
            });
            const childTlStartLabel = this.createAnimLabel({
                order,
                prefix: CHILD_TL,
                suffix: ANIM_STATE.START,
            });
            masterTimeline.addLabel(childTlStartLabel);
            animList.forEach((animEffectList, index) => {
                const label = this.createAnimLabel({
                    order,
                    prefix: CHILD_TL,
                    suffix: ANIM_STATE.START,
                    index,
                });
                childTimeline.addLabel(label);
                animEffectList.forEach((animModel: IAnimModel) => {
                    const animTargetKey = animModel?.elementModel?.key;
                    const animModelKey = animModel.key;
                    const animStopAtLength = animModel.audioNumberedSlide;
                    if (!animTargetKey) return;
                    const animTarget = getTargetComponent(animTargetKey, slideComponent, layerService);
                    if (!animTarget || animTarget.factoryOptions.model.hidden) return;
                    const { fromStyle, toStyle } = this.getFormatStyle(animModel, animTarget);
                    if (!this.originalStyleMap.get(animTargetKey)) {
                        const originalStyle = this.getOriginalStyleFromTarget(animTarget, fromStyle);
                        this.originalStyleMap.set(animTargetKey, originalStyle);
                    }
                    const presetClass = animModel?.props?.presetClass;
                    const animType = animModel?.props?.animType;
                    const subType = animModel?.props.subType;
                    const includeMotion = this.includeMotion(animModel.props.animType as AnimType);
                    const tempStyle: any = fromStyle;
                    // 出现动画的持续时间为1ms，持续时间过短导致预览时无法看到效果
                    // office中，可设置的最小时间为10ms，以该时长为判断条件，预览态下小于该时间的将其时间置为30fps下，一帧的时间，保证看到效果
                    // 理想情况下设置为33ms可以保证看到效果，但从侧边栏入口进行动画切换的时候侧边栏有长任务耗时60-100ms，先简单处理，进一步加长时间
                    let duration = animModel?.props?.duration;
                    if (this.options?.preview && duration < TIME_CONSTANTS.OFFICE_MIN_TIME) {
                        duration = TIME_CONSTANTS.ONE_FRAME_MIN_TIME;
                    }
                    duration /= 1000;
                    // @ts-ignore
                    const animIns: Animation = {};
                    const animationProps: any = {
                        type: 'fromTo',
                        target: tempStyle,
                        fromStyle,
                        toStyle: {
                            duration,
                            delay: animModel?.props?.delay / 1000,
                            id: animModel?.key,
                            onStart: this.handleAnimStart.bind(this),
                            onUpdate: this.handleAnimUpdate.bind(this),
                            onComplete: this.handleAnimComplete.bind(this),
                            onStartParams: [{ animTarget, order, presetClass, animType, animModel,
                                subType, fromStyle, toStyle, animTargetKey, animModelKey, animStopAtLength }],
                            onUpdateParams: [
                                { tempStyle, animModel, animTarget, fromStyle,
                                    isPathType: includeMotion, order, animIns, duration },
                            ],
                            onCompleteParams: [{ animModel, animTarget, order, fromStyle }],
                            callbackScope: this,
                            ease: toStyle.easing === 'easeOutBack' ? 'back.out(1)' : 'none',
                            immediateRender: false,
                        },
                    };
                    // 如果是关键帧动画
                    if (Array.isArray(toStyle)) {
                        animationProps.toStyle.keyframes = toStyle;
                    } else {
                        animationProps.toStyle = {
                            ...animationProps.toStyle,
                            ...toStyle,
                        };
                    }
                    // 如果是路径动画或者包含 p:AnimMotion 路径动画
                    if (includeMotion) {
                        const path = animModel.getAnimPath(animModel.id || '');
                        path && (animationProps.toStyle.pathAnime = { path });
                        // 路径动画变换的属性固定为x与y，且无法通过getDifferentProperty计算出
                        let differentProperties: string[] = [];
                        if (!this.isPathAnim(animType as AnimType)) {
                            // 针对非路径动画但包含 animMotion 属性的需要合并起始和终止样式
                            differentProperties = getDifferentProperty(fromStyle, toStyle);
                        }
                        this.differentPropertyMap.set(
                            animModel.key,
                            [...differentProperties, ANIM_CANVAS_PROPERTY.X, ANIM_CANVAS_PROPERTY.Y],
                        );
                    } else {
                        const differentProperties = getDifferentProperty(fromStyle, toStyle);
                        this.differentPropertyMap.set(animModel.key, differentProperties);
                    }
                    const animationInstance = new Animation(animationProps);
                    animIns.instance = animationInstance.instance;
                    childTimeline.add(animationInstance, label);
                    const endLabel = this.createAnimLabel({
                        order,
                        prefix: CHILD_TL,
                        suffix: ANIM_STATE.COMPLETE,
                        index,
                    });
                    childTimeline.addLabel(endLabel);
                });
            });
            masterTimeline.add(childTimeline);
            const childTlEndLabel = this.createAnimLabel({
                order,
                prefix: CHILD_TL,
                suffix: ANIM_STATE.COMPLETE,
            });
            masterTimeline.addLabel(childTlEndLabel);
        });
        masterTimeline.addLabel(ANIM_STATE.COMPLETE);
        return masterTimeline;
    }

    /**
     * 清空当前timeline下的所有子timeline以及所有子动画
     */
    public clearMasterTimeline(): void {
        this.masterTimeline.clear();
    }

    /**
     * 清空全局timeline
     */
    public clearGlobalTimeline(): void {
        Timeline.globalTimeline.clear();
    }

    /**
     * 获取元素与动画的映射
     * @param animList [AnimationModel1, AnimationModel2, AnimationModel3]
     * @returns Map {targetKey => [AnimationModel1, AnimationModel2]}
     */

    public getTargetAnimMap(animList: IAnimList): Map<string, IAnimList> {
        const targetAnimMap = new Map();
        animList.forEach((animModel: IAnimModel) => {
            const targetKey = animModel?.elementModel?.key;
            const animModelList = targetAnimMap.get(targetKey);
            if (animModelList) {
                animModelList.push(animModel);
            } else {
                targetAnimMap.set(targetKey, [animModel]);
            }
        });

        return targetAnimMap;
    }

    /**
     * 获取对应动画的初始样式
     * @param animModel 动画uiModel
     * @returns IAnimSimpleStyle
     * 备注： fromStyle的属性值没有数组形式，toStyle的属性值在关键帧动画时是数组形式
     */
    public getFromStyle(animModel: IAnimModel, animTarget: ComponentAnimWrapper): IAnimSimpleStyle | IFormatStyle[] {
        const tempFromStyle = initFromStyle(animModel, PLAY_MODE.edit, animTarget);
        if (!isUndefined(tempFromStyle.rotate)) {
            tempFromStyle.rotate -= this.getElementRotate(animModel);
        }
        return this.formatStyle(tempFromStyle);
    }

    /**
     * 获取滤镜类动画初始状态
     * @param animType 动画类型
     * @param subType 方向等其他子属性
     * @param presetClass 动画大类
     * @returns Partial<IAnimFilterOptions>
     */
    public getFromFilterOptions(
        subType: PresetSubtype,
        presetClass: Action,
        filterType?: AnimFilters,
    ): Partial<IAnimFilterOptions> {
        if (!filterType) return {};

        return {
            rate: 0,
            direction: subType,
            action: presetClass,
        };
    }

    /**
     * 子时间线onUpdate回调
     */
    protected handleChildTlUpdate(animList: IAnimModel[][]): void {
        // 同一时间内只有音频或者视频控制类动画播放时，不必进行canvas的渲染。
        if (this.isMediaControlAnim(animList)) return;
        this.syncRender();
    }
    /**
     * 子时间线onStart回调
     */
    protected handleChildTlStart(animList: AnimationModel[][]): void {
        animList.forEach((animLst) => {
            animLst.forEach((animationModel) => {
                const { elementModel } = animationModel;
                elementModel && this.mediaManager.handleAnimationStart(elementModel as IElementBaseModel);
            });
        });
    }

    /**
     * 子时间线onComplete回调
     */
    protected handleChildTlComplete(
        order: number,
        animList: AnimationModel[][],
    ): void {
        animList.forEach((animLst) => {
            animLst.forEach((animationModel) => {
                const { elementModel } = animationModel;
                elementModel && this.mediaManager.handleAnimationEnd(elementModel as IElementBaseModel);
            });
        });
    }

    /**
     * 总时间线onStart回调
     */
    protected handleMasterStart(): void {
        if (this.slideComponent?.options.getApplication?.()) {
            this.slideComponent.options.getApplication().autoRender = false;
            if (this.options.preview && this.options.editorAnimPreviewManager) {
                this.options.editorAnimPreviewManager.isAnimPreviewing = true;
            }
        }
    }

    /**
     * 总时间线onComplete回调, 编辑态和演示态各自重写该方法
     */
    protected handleMasterComplete(animsOrderMap: Map<number, IAnimModel[][]>): void {}

    protected resumeAutoRender(): void | undefined {
        const application = this.slideComponent?.options.getApplication?.();
        if (!application) return;
        application.render();
        application.autoRender = true;
    }

    protected handleAnimStart(params: IAnimStartParams): void {
        this.checkAnimInDevMode(params);
        // 单个动画开始时，处理音视频动画、iframe动画、滤镜类动画（如百叶窗）
        this.handleMediaEleAnimStart(params);
        this.handleIframeAnimStart(params);
        this.handleFilterAnimStart(params);
        this.handleColorAnimStart(params);
        this.makeEntrAnimVisibleOnStart(params);
    }

    protected handleAnimComplete(params: IAnimCompleteParams): void {
        const { animTarget, animModel } = params;
        const animModelKey = animModel?.key;
        const animTargetKey = animModel?.elementModel?.key;
        const animType = animModel?.props.animType;
        const presetClass = animModel?.props.presetClass;
        const animFilterType = this.animFiltersManager.getAnimFilterType(animType);
        const nextAnimIsEntry = this.isNextAnimEntry(params);
        const currentAnimIsExit = presetClass === presetClassTypes.EXIT;

        if (animFilterType) {
            this.animFiltersManager
                .handleAnimFiltersComplete(
                    animFilterType as AnimFilters,
                    animTarget,
                    animModel?.props.presetClass  as Action || Action.ENTR,
                    currentAnimIsExit && nextAnimIsEntry,
                );
            return;
        }
        const currentStyle = this.getCurrentStyle(animTarget);
        const lastStyle = { ...(this.lastStyleMap.get(animTargetKey) || this.originalStyleMap.get(animTargetKey)) };
        const differentProperties = this.differentPropertyMap.get(animModelKey);
        differentProperties.forEach((key) => {
            lastStyle[key] = currentStyle[key];
        });

        if (nextAnimIsEntry) {
            this.handleLastStyle(animTarget, animTargetKey, lastStyle);
            this.handleFilterAnimBeforeCurrent(params);
        }
        this.lastStyleMap.set(animTargetKey, lastStyle);
    }

    protected completeAnimMediaControl(params: IAnimCompleteParams): void {
        const { animTarget, animModel: { props: { presetClass } } } = params;
        if (presetClass !== presetClassTypes.MEDIA) return;
        const elementModel = animTarget?.factoryOptions?.model;
        // 动画的目标元素不存在或者不是音视频等dom兼容元素，直接return
        if (!elementModel || !isMediaElement(elementModel)) return;
        this.animMediaControl?.completeMediaControl({ elementModel, presetClass });
    }

    protected handleAnimUpdate(animUpdateParams: IAnimUpdateParams): void {
        const { tempStyle, animModel, animTarget, fromStyle, isPathType, animIns, duration } = animUpdateParams;
        const animModelKey = animModel?.key;
        const animTargetKey = animModel?.elementModel?.key;
        const animType = animModel?.props.animType;
        const presetClass = animModel?.props.presetClass;
        this.clampRGBA(tempStyle);
        // 媒体控制类动画不必进行update
        if (presetClass === presetClassTypes.MEDIA) return;
        const animFilterType = this.animFiltersManager.getAnimFilterType(animType);
        if (animFilterType) {
            this.animFiltersManager.handleAnimFiltersUpdate({ animTarget, animFilterType, animIns, duration });
        }

        const originalStyle = this.originalStyleMap.get(animTargetKey);
        const lastStyle = this.lastStyleMap.get(animTargetKey);
        const mergedStyle = this.getMergedStyle(tempStyle, isPathType ? originalStyle : lastStyle || originalStyle);
        const differentProperty = this.differentPropertyMap.get(animModelKey);
        // if (this.isPathTypeFinishBug(isPathType, tempStyle)) {
        //     // 奇怪的bug，路径动画在反复上翻与下翻后，再次进行播放，在结束的那次update时tempStyle会变成初始状态，手动拦截一下
        //     return;
        // }
        differentProperty.forEach((key) => {
            if ([ANIM_CANVAS_PROPERTY.X, ANIM_CANVAS_PROPERTY.Y].includes(key)) {
                animTarget[key] = Math.floor(mergedStyle[key]);
            } else {
                animTarget[key] = mergedStyle[key];
            }
        });
        this.updateColorAnim(animTarget, animType as AnimType);
        /**
         * 渲染层使用变换矩阵来对canvas元素进行transform相关的变换，在改变元素属性时
         * 原点位置会影响变换矩阵的计算结果，由于animWrapper的默认原点为(0, 0),变换结果不是符合预期的
         * 故在每次update的时候需要调整原点位置到元素中心(有transformOrigin的额外进行调整)
         * 又由于调整原点后会带来位移变换的副作用，需要调整x与y进行负向补偿回来
         */
        const style = lastStyle || originalStyle;
        animTarget.pivotX = style.pivotX;
        animTarget.pivotY = style.pivotY;

        const tmpStyle = this.tempStyleMap.get(animTargetKey) || originalStyle;
        if (!differentProperty.includes(ANIM_CANVAS_PROPERTY.X)) {
            animTarget.x = tmpStyle.x;
        }
        if (!differentProperty.includes(ANIM_CANVAS_PROPERTY.Y)) {
            animTarget.y = tmpStyle.y;
        }
        if (fromStyle.transformOrigin) {
            this.adjustTransformOrigin(fromStyle, animTarget);
        }
        const currentStyle = this.getCurrentStyle(animTarget);
        this.tempStyleMap.set(animTargetKey, currentStyle);
    }

    /**
     * 生成播放顺序与动画的映射对象，该映射对象的value是一个二维数组
     * 其中，首先对动画按播放顺序分组，其次同组动画根据clickEffect和afterEffect再次分组
     * @param anims
     * @returns order => [[clickEffectAnim, withEffectAnim], [afterEffectAnim]， [afterEffectAnim]]
     */
    protected getAnimsByOrder(anims: Array<IAnimModel> | undefined): Map<number, Array<Array<IAnimModel>>> {
        const orderMap = new Map();
        anims?.forEach((animModel: IAnimModel) => {
            const order = animModel?.playOrder;
            const nodeType = animModel?.props?.nodeType;
            const sameOrderAnims = orderMap.get(order);
            if (sameOrderAnims) {
                if (nodeType === nodeTypes.WITH_EFFECT) {
                    const lastEffectList = sameOrderAnims[sameOrderAnims.length - 1];
                    lastEffectList.push(animModel);
                } else {
                    sameOrderAnims.push([animModel]);
                }
            } else {
                orderMap.set(order, [[animModel]]);
            }
        });

        return orderMap;
    }

    protected getOriginalStyleFromTarget(animTarget, fromStyle): IAnimSimpleStyle {
        const staticProperty = {
            alpha: 1,
            scaleX: 1,
            scaleY: 1,
            skewX: 0,
            skewY: 0,
            angle: 0,
            rotation: 0,
            visible: true,
        };
        let { x, y, pivotX, pivotY } = animTarget;
        const { left, top, width, height } = animTarget.factoryOptions.frame;
        x = left + (width / 2);
        pivotX = left + (width / 2);
        y = top + (height / 2);
        pivotY = top + (height / 2);

        return {
            ...staticProperty,
            x,
            y,
            pivotX,
            pivotY,
            visible: animTarget.visible,
            ...this.getColorOriginalStyle(animTarget.factoryOptions.model, fromStyle) };
    }

    /**
     * 创建动画label标签
     * @returns label
     */
    protected createAnimLabel = (params: ILabelParams): string => {
        const { order, prefix, suffix, index } = params;
        let label = `${prefix}${order}-label${index}-${suffix}`;
        if (isUndefined(index)) {
            label = `${prefix}${order}-${suffix}`;
        }
        return label;
    };

    protected hasValidProperty(obj: IAnimSimpleStyle, key: string): boolean {
        return Object.prototype.hasOwnProperty.call(obj, key) && !isUndefined(obj[key]) && !isNaN(obj[key]);
    }

    protected getMergedStyle(tempStyle: IAnimSimpleStyle, baseStyle: IAnimSimpleStyle): IAnimSimpleStyle {
        if (!baseStyle) return tempStyle;
        const mergedStyle: any = {};
        const { ALPHA, X, Y, SCALE_X, SCALE_Y, ANGLE } = ANIM_CANVAS_PROPERTY;
        if (this.hasValidProperty(tempStyle, ALPHA)) {
            mergedStyle.alpha = Number(tempStyle.alpha) * Number(baseStyle.alpha);
        }
        if (this.hasValidProperty(tempStyle, ANGLE)) {
            mergedStyle.angle = Number(tempStyle.angle);
        }
        if (this.hasValidProperty(tempStyle, SCALE_X)) {
            mergedStyle.scaleX = Number(tempStyle.scaleX) * Number(baseStyle.scaleX);
        }
        if (this.hasValidProperty(tempStyle, SCALE_Y)) {
            mergedStyle.scaleY = Number(tempStyle.scaleY) * Number(baseStyle.scaleY);
        }
        if (this.hasValidProperty(tempStyle, X)) {
            mergedStyle.x = Math.round(Number(tempStyle.x) + Number(baseStyle.x));
        }
        if (this.hasValidProperty(tempStyle, Y)) {
            mergedStyle.y = Math.round(Number(tempStyle.y) + Number(baseStyle.y));
        }

        if (tempStyle[AnimCanvasProperty.FILL_COLOR]) {
            mergedStyle.fillColor = tempStyle[AnimCanvasProperty.FILL_COLOR];
        }
        if (tempStyle[AnimCanvasProperty.FONT_COLOR]) {
            mergedStyle.fontColor = tempStyle[AnimCanvasProperty.FONT_COLOR];
        }
        if (tempStyle[AnimCanvasProperty.LINE_COLOR]) {
            mergedStyle.lineColor = tempStyle[AnimCanvasProperty.LINE_COLOR];
        }

        if (tempStyle[AnimCanvasProperty.FONT_SIZE]) {
            mergedStyle.fontSize = tempStyle[AnimCanvasProperty.FONT_SIZE];
        }
        return mergedStyle;
    }

    protected formatStyle(styleObj: any): IAnimSimpleStyle | IFormatStyle[] {
        const tempStyleObj = { ...styleObj };
        const { TRANSLATE_X, TRANSLATE_Y, OPACITY, ROTATE } = ANIM_PROPERTY;
        const { X, Y, ALPHA, ANGLE } = ANIM_CANVAS_PROPERTY;
        const attrNames = [
            { initial: TRANSLATE_X, formatted: X },
            { initial: TRANSLATE_Y, formatted: Y },
            { initial: OPACITY, formatted: ALPHA },
            { initial: ROTATE, formatted: ANGLE },
        ];
        // 动画中使用的属性名与渲染层不一致，需要对key值进行替换
        attrNames.forEach((attrName) => {
            const value = styleObj?.[attrName.initial];
            if (value !== undefined) {
                tempStyleObj[attrName.formatted] = value;
                Reflect.deleteProperty(tempStyleObj, attrName.initial);
            }
        });
        // 对传入的每个key进行检查，检查是否有数组类型的value，如果有说明是关键帧动画，记录其最大的长度，并将其转换为canvasStyle
        // 关键帧动画的toStyle是一个数组，每个元素中存这次需要变换的所有属性
        let maxLength = 0;
        Object.keys(tempStyleObj).forEach((key) => {
            if (Array.isArray(tempStyleObj[key]) && maxLength < tempStyleObj[key].length) {
                maxLength = tempStyleObj[key].length;
            }
        });
        if (!maxLength) {
            return tempStyleObj;
        }
        const tempStyleObjArr: any = [];
        for (let i = 0; i < maxLength;i++) {
            const item: any = {};
            Object.keys(tempStyleObj).forEach((key) => {
                if (tempStyleObj[key].length) {
                    item[key] = tempStyleObj[key][i].value;
                    const { duration } = tempStyleObj[key][i];
                    item.duration = duration / 1000;
                } else {
                    item[key] = tempStyleObj[key];
                }
            });
            tempStyleObjArr.push(item);
        }
        return tempStyleObjArr;
    }

    protected getCurrentStyle(animTarget: IAnimTarget): IAnimSimpleStyle {
        const { alpha, scaleX, scaleY, skewX, skewY, angle, rotation, x, y, pivotX, pivotY, visible } = animTarget;
        const regularAttrStyle = { alpha, scaleX, scaleY, skewX, skewY,
            angle, rotation, x, y, pivotX, pivotY, visible };
        const colorHandlerMap = this.getColorHandlerMap(animTarget.factoryOptions.model);
        const colorStyle = ATTR_LIST.reduce((accStyle, curName) => {
            if (!animTarget[curName]) {
                animTarget[(curName as string)] = colorHandlerMap[curName]();
            }
            return { ...accStyle, [curName]: animTarget[curName] };
        }, {});
        return  { ...regularAttrStyle, ...colorStyle };
    }

    /**
     * 获取不同颜色类型的映射表
     * @param elementModel 动画目标元素UIModel数据
     * @returns 映射表
     */
    protected getColorHandlerMap(elementModel: IElementBaseModel):
    Record<ColorPropertyValues, () => string| number | undefined> {
        return {
            [AnimCanvasProperty.FILL_COLOR]: () => getFillColor(elementModel)?.rgba,
            [AnimCanvasProperty.FONT_COLOR]: () =>  getFontColor(elementModel)?.rgba,
            [AnimCanvasProperty.LINE_COLOR]: () => getLineColor(elementModel)?.rgba,
            [AnimCanvasProperty.FONT_SIZE]: () => getFontSize(elementModel),
        };
    }

    /**
     * 获取动画目标元素的旋转角度
     * @param animModel IAnimModel
     * @returns number 旋转角度
     */
    protected getElementRotate(animModel: IAnimModel): number {
        return animModel?.elementModel?.props?.style?.rotate || 0;
    }

    /**
     * 设置stopUpdateHitRegion的值
     * value为true则使用上次的事件响应热区，不必重新渲染事件热区
     * value为false则触发事件区域热区的重新渲染
     * @param value
     * @returns void
     */
    protected setStopUpdateHitRegion(value: boolean): void {
        const app = this.slideComponent?.options.getApplication?.();
        if (!app) return;
        app.stopUpdateHitRegion = value;
    }

    protected syncRender() {
        // 因为属性修改后，会在下一帧渲染，由于动画层raf的触发时机在渲染层raf触发时机之前，
        // 导致部分帧会命中渲染层的requestRender中的状态为true的逻辑，进而会导致渲染层不渲染，从而空了一帧，帧率下降为30fps
        // 当元素属性修改时，直接进行同步渲染
        const application = this.slideComponent?.options.getApplication?.();
        if (application) {
            // 动画需要执行全局渲染，且动画没有使用requestRender（requestRender会自动调度全局/局部渲染），需要主动标记一下
            // todo: 其实理论上所有渲染都可以走到局部渲染，即使是分层后的动画，这块之后研究下
            application.allRefresh = true;
            application.render();
        }
    }

    private convertAnimToKeyframeAnim(animModel, tempFromStyle, tempToStyle) {
        // 旋转与基本旋转在dom动画中使用rotateY与rotateX属性进行操作
        // canvas为2d效果，需要手动转成关键帧类型的动画，利用scaleX的变换模拟出类似的效果
        const fromStyle = tempFromStyle;
        let toStyle = tempToStyle;
        const duration = animModel?.props?.duration / 1000;
        if (animModel?.props.animType === AnimType.EnterRotate) {
            const { alpha } = toStyle;
            fromStyle.scaleX = 0;
            toStyle = [
                { ...toStyle, scaleX: 1, duration: duration * 0.2, alpha: alpha * 0.2 },
                { ...toStyle, scaleX: -1,  duration: duration * 0.4, alpha: alpha * 0.6 },
                { ...toStyle, scaleX: 1,  duration: duration * 0.4, alpha },
            ];
        } else if (animModel?.props.animType === AnimType.ExitRotate) {
            const { alpha } = fromStyle;
            fromStyle.scaleX = 1;
            toStyle = [
                { ...toStyle, scaleX: -1,  duration: duration * 0.4, alpha: alpha * 0.6 },
                { ...toStyle, scaleX: 1, duration: duration * 0.4, alpha: alpha * 0.2 },
                { ...toStyle, scaleX: 0,  duration: duration * 0.2, alpha: 0 },
            ];
        } else if (animModel?.props.animType === AnimType.EntrBasicSwivel) {
            if (fromStyle.rotateX === toStyle.rotateX) {
                fromStyle.scaleX = 0;
                fromStyle.alpha = 1;
                toStyle = [
                    { ...toStyle, scaleX: 1, duration: duration * 0.2 },
                    { ...toStyle, scaleX: -1,  duration: duration * 0.4 },
                    { ...toStyle, scaleX: 1,  duration: duration * 0.4 },
                ];
            } else {
                fromStyle.scaleY = 0;
                fromStyle.alpha = 1;
                toStyle = [
                    { ...toStyle, scaleY: 1, duration: duration * 0.2 },
                    { ...toStyle, scaleY: -1,  duration: duration * 0.4 },
                    { ...toStyle, scaleY: 1,  duration: duration * 0.4 },
                ];
            }
        } else if (animModel?.props.animType === AnimType.ExitBasicSwivel) {
            if (fromStyle.rotateX === toStyle.rotateX) {
                fromStyle.scaleX = 1;
                toStyle.alpha = 1;
                toStyle = [
                    { ...toStyle, scaleX: -1,  duration: duration * 0.4 },
                    { ...toStyle, scaleX: 1, duration: duration * 0.4 },
                    { ...toStyle, scaleX: 0,  duration: duration * 0.2 },
                ];
            } else {
                fromStyle.scaleY = 1;
                toStyle.alpha = 1;
                toStyle = [
                    { ...toStyle, scaleY: -1,  duration: duration * 0.4 },
                    { ...toStyle, scaleY: 1, duration: duration * 0.4 },
                    { ...toStyle, scaleY: 0,  duration: duration * 0.2 },
                ];
            }
        }
        return { fromStyle, toStyle };
    }

    private getFormatStyle(animModel: IAnimModel, animTarget: ComponentAnimWrapper) {
        const tempToStyle = this.getToStyle(animModel);
        const tempFromStyle = this.getFromStyle(animModel, animTarget);
        const { fromStyle, toStyle } = this.convertAnimToKeyframeAnim(animModel, tempFromStyle, tempToStyle);
        this.handleColorFromStyle(fromStyle, toStyle);
        return { fromStyle, toStyle };
    }

    private  handleColorFromStyle(fromStyle: IAnimSimpleStyle, toStyle: IAnimSimpleStyle): void {
        const colorAttrMap = {
            [AnimCanvasProperty.FILL_COLOR]: COLOR.white,
            [AnimCanvasProperty.LINE_COLOR]: COLOR.black,
        };

        Object.entries(colorAttrMap).forEach(([key, value]) => {
            if (!fromStyle[key] && this.keyInToStyle(key, toStyle)) {
                fromStyle[key] = value;
            }
        });
    }

    private keyInToStyle(key: string, toStyle: IAnimSimpleStyle | IAnimSimpleStyle[]): boolean {
        if (!Array.isArray(toStyle))  return !!toStyle[key];

        return toStyle.some(styleObj => styleObj[key]);
    }


    private getToStyle(animModel: IAnimModel) {
        const tempToStyle: any = initToStyle(animModel, PLAY_MODE.edit);
        const originRotate = this.getElementRotate(animModel);
        if (Array.isArray(tempToStyle.rotate)) {
            tempToStyle.rotate.forEach((item) => {
                item.value -= originRotate;
            });
        } else if (!isUndefined(tempToStyle.rotate)) {
            tempToStyle.rotate -= originRotate;
        }
        animModel.setAnimModelToStyle(tempToStyle);
        return this.formatStyle(tempToStyle);
    }

    // 渲染层目前不支持直接设置transformOrigin,可通过调整pivot与translate达到相同的效果
    private adjustTransformOrigin(fromStyle, animTarget) {
        const tmpTarget = animTarget;
        const { left, top, width, height } = tmpTarget.factoryOptions.frame;
        if (fromStyle.transformOrigin === 'center top') {
            tmpTarget.pivotY = top;
            tmpTarget.y = top;
        } else if (fromStyle.transformOrigin === 'center bottom') {
            tmpTarget.pivotY = top + height;
            tmpTarget.y = top + height;
        } else if (fromStyle.transformOrigin === 'left center') {
            tmpTarget.pivotX = left;
            tmpTarget.x = left;
        } else if (fromStyle.transformOrigin === 'right center') {
            tmpTarget.pivotX = left + width;
            tmpTarget.x = left + width;
        }
    }

    private isPathTypeFinishBug(isPathType, tempStyle) {
        return isPathType && !tempStyle.x && !tempStyle.y;
    }

    private getRegion() {
        const { isPlaying } = this.playManager;
        const region = isPlaying ? RegionType.PLAYER : RegionType.EDITOR;
        return region;
    }

    /**
     * 第三方插件动画
     */
    private handleIframeAnimStart(params: IAnimStartParams): void {
        const elementModel = params?.animTarget?.factoryOptions?.model;
        if (!isIframeShapeModel(elementModel)) return;
        this.mediaManager.changeIframeDisplayState(false);
    }

    /**
     * 音视频动画，包括控制类动画和非控制类普通动画
     */
    private handleMediaEleAnimStart(params: IAnimStartParams): void {
        const { animTarget, presetClass, animType, animStopAtLength } = params;
        const elementModel =  animTarget?.factoryOptions?.model;
        if (isGroupModel(elementModel)) {
            ElementsAnalyze.getTypesOfElements(elementModel.children, [UIModelType.IMAGE])
                .forEach((model) => {
                    // 组合中的音视频元素也需要响应回调
                    if (isMediaElement(model)) {
                        AnimMediaControlFactory.getInstance(this.getRegion(), getMediaType(model))
                            ?.startMediaControl({ elementModel: model, presetClass, animType, animStopAtLength });
                    }
                });
        }
        if (!isMediaElement(elementModel)) return;
        this.animMediaControl =  AnimMediaControlFactory.getInstance(this.getRegion(), getMediaType(elementModel));
        this.animMediaControl?.startMediaControl({ elementModel, presetClass, animType, animStopAtLength });
    }

    /**
     * 滤镜类动画，如百叶窗、棋盘
     */
    private handleFilterAnimStart(params: IAnimStartParams): void {
        const { animTarget, presetClass, animType, subType } = params;
        const animFilterType = this.animFiltersManager.getAnimFilterType(animType);
        if (!animFilterType) return;
        this.animFiltersManager.handleAnimFiltersStart(
            animFilterType as AnimFilters,
            animTarget,
            { action: presetClass as Action, direction: subType || PresetSubtype.Across },
        );
    }

    /**
     * 颜色动画开始时重置颜色值为上一个颜色动画的最终帧
     * @param params IAnimStartParams
     */
    private handleColorAnimStart(params: IAnimStartParams): void {
        const { fromStyle, animTarget, animModel, toStyle } = params;
        const animTargetKey = animModel.elementModel.key;
        if (!colorAnimTypeList.includes(animModel.props.animType as AnimType)) return;
        animTarget.factoryOptions.anim = {};
        const lastStyle = this.lastStyleMap.get(animTargetKey);
        ALL_COLOR_LIST.forEach((attr) => {
            if (fromStyle[attr] && animTarget[attr] && lastStyle?.[attr]) {
                this.reorganizeStyle({
                    animModel,
                    toStyle,
                    fromStyle,
                    attrName: attr,
                    attrValue: lastStyle[attr],
                });
            }
        });
    }


    /**
    * 颜色动画开始时重新计算fromstyle和toStyle
    * 同一个元素设置多个颜色动画时，若需要变化相同的属性，后置的动画需要依据前置动画的终态
    * 重新计算fromstyle和toStyle
    */

    private reorganizeStyle({ animModel, toStyle, attrName, fromStyle, attrValue }: {
        animModel: IAnimModel,
        toStyle: IToStyle,
        fromStyle: IAnimSimpleStyle,
        attrName: string,
        attrValue: string,
    }) {
        fromStyle[attrName] = attrValue;
        const animTypeList = [
            AnimType.ComplementaryColor,
            AnimType.ComplementaryColor2,
            AnimType.ContrastingColor,
            AnimType.Darken,
            AnimType.Desaturate,
            AnimType.Lighten,
        ];
        if (!animTypeList.includes(animModel.props.animType as AnimType)) return;
        // 重新计算toStyle
        const hsl = (animModel.sdkData as IEGTimeNodeChoice)?.par?.cTn?.childTnLst?.egTimeNodeChoice?.[0]?.animClr?.by
            ?.hsl;
        const startColor = new TDColor({ rgbaStr: fromStyle[attrName] });
        const color = calculateHSLColor(hsl, startColor);
        if (!color) return;
        if (Array.isArray(toStyle)) {
            toStyle.forEach((item) => {
                if (!item[attrName]) return;
                item[attrName] = color;
            });
        } else {
            toStyle[attrName] = color;
        }
    }

    /**
     * 判断一组动画是否全都是媒体控制类动画
     * @param animList IAnimModel[][]
     * @returns boolean
     */
    private isMediaControlAnim(animList: IAnimModel[][]): boolean {
        const isMedia = animModel => animModel?.props.presetClass === presetClassTypes.MEDIA;
        return animList.every(animEffectList => animEffectList.every(isMedia));
    }

    /**
     * 重新组织动画uiModel数据，剔除隐藏元素
     * @param animList IAnimModel[]
     * @param slideModel ISlideModel
     * @returns IAnimModel[]
     */
    private reorganizeAnimList(animList: IAnimModel[], slideModel: ISlideModel): IAnimModel[] {
        const list: IAnimModel[] = [];
        animList.forEach((animModel) => {
            const { spid, props: { nodeType } } = animModel;
            const isVisible = isElementVisible(spid, slideModel);
            if (!isVisible) return;
            const len = list.length;
            const lastAnimModelOrder = list[len - 1]?.playOrder ?? 0;
            if (nodeType === nodeTypes.WITH_EFFECT || nodeType === nodeTypes.AFTEREFFECT) {
                animModel.playOrder = lastAnimModelOrder;
            } else {
                animModel.playOrder = lastAnimModelOrder + 1;
            }
            list.push(animModel);
        });

        return list;
    }

    /**
     * 动画update时更新颜色类动画
     * @param animTarget 动画目标元素
     */
    private updateColorAnim(animTarget: IAnimTarget, animType: AnimType): void {
        if (!colorAnimTypeList.includes(animType)) return;
        const { factoryOptions } = animTarget;
        const needUpdateFontList = [AnimCanvasProperty.FONT_COLOR, AnimCanvasProperty.FONT_SIZE];
        const list = animType === AnimType.GrowWithColor ? ATTR_LIST : ALL_COLOR_LIST;
        list.forEach((key) => {
            if (!animTarget[key]) return;
            factoryOptions.anim = {
                ...factoryOptions.anim,
                [key]: animTarget[key],
                ...(needUpdateFontList.includes(key) ? { needUpdateFont: true } : {}),
            };
        });
        animTarget.factoryUpdateComponent();
    }

    /**
     * 将颜色值限定在取值范围内
     * @param styleObj IAnimSimpleStyle
     */
    private clampRGBA(styleObj: IAnimSimpleStyle): void {
        ALL_COLOR_LIST.forEach((key) => {
            if (!styleObj[key]) return;
            const value =  normalizeRgba(styleObj[key] as string);
            if (!value) return;
            styleObj[(key as string)] = value;
        });
    }

    /**
     * 获取颜色动画的初始样式
     * @param model 动画目标元素uiModel
     * @param fromStyle 动画的初始样式
     * @returns 颜色动画数据
     */
    private getColorOriginalStyle(model: IElementBaseModel, fromStyle: IAnimSimpleStyle): IAnimSimpleStyle {
        let  colorStyle: IAnimSimpleStyle = {};
        const handlerMap = {
            [AnimCanvasProperty.FILL_COLOR]: () => getFillColor(model)?.rgba,
            [AnimCanvasProperty.FONT_COLOR]: () => getFontColor(model)?.rgba,
            [AnimCanvasProperty.LINE_COLOR]: () => getLineColor(model)?.rgba,
            [AnimCanvasProperty.FONT_SIZE]: () => getFontSize(model),
        };
        Object.entries(handlerMap).forEach(([key, handler]) => {
            if (!fromStyle[key] || !handler?.()) return;
            colorStyle = {
                ...colorStyle,
                [key]: handler(),
            };
        });

        return colorStyle;
    }

    private isPathAnim = (animType: AnimType) => Object.values(PATH_ANIM_TYPES).includes(animType);

    // 判断动画属性中是否包含 p:animMotion
    private includeMotion = (animType: AnimType) => [
        ...Object.values(PATH_ANIM_TYPES),
        AnimType.CurveDown,
        AnimType.CurveUp,
    ].includes(animType);

    /**
     * 判断元素在当前页的下一个动画是否为入场动画
     * @param params IAnimCompleteParams
     * @returns boolean
     * 若下一个动画是入场动画，则需要将元素的透明度、scaleX、scaleY设置为1
     */
    private isNextAnimEntry(params: IAnimCompleteParams): boolean {
        // 此处的order是剔除隐藏元素之后的playOrder
        const { animModel, order: currentOrder } = params;
        if (!this.animTargetKeyOrderMap) {
            this.animTargetKeyOrderMap = getAnimTargetKeyOrderMap(this.slideComponent?.options.slideModel);
        }

        const orderListOfCurrentTarget = this.animTargetKeyOrderMap?.get(animModel.elementModel.key);
        const nextOrder = orderListOfCurrentTarget?.find(order => order > currentOrder);
        if (nextOrder === undefined) return false;
        const anim = animModel.elementModel.animList.find(model => model.playOrder === nextOrder);
        return anim?.props?.presetClass === presetClassTypes.ENTR;
    }

    /**
     * 处理动画结束帧样式
     * @param animTarget 动画目标元素canvas component
     * @param animTargetKey  动画目标元素key
     * @param lastStyle 动画结束帧样式
     */
    private handleLastStyle(animTarget: IAnimTarget, animTargetKey: string, lastStyle: IAnimSimpleStyle) {
        if (lastStyle.alpha === 0) {
            lastStyle.alpha = 1;
        }

        if (lastStyle.scaleX === 0) {
            lastStyle.scaleX = 1;
            animTarget.scaleX = 1;
        }

        if (lastStyle.scaleY === 0) {
            lastStyle.scaleY = 1;
            animTarget.scaleY = 1;
        }
        const originalStyle = this.originalStyleMap.get(animTargetKey);
        const tmpStyle = this.tempStyleMap.get(animTargetKey);
        if (tmpStyle) {
            tmpStyle.x = originalStyle.x;
            tmpStyle.y = originalStyle.y;
        }
        lastStyle.x = originalStyle.x;
        lastStyle.y = originalStyle.y;
        if (animTarget.angle !== originalStyle.angle) {
            lastStyle.angle = originalStyle.angle;
            animTarget.angle = originalStyle.angle;
        }
    }

    /**
     * 判断在当前动画之前是否有退出滤镜动画
     * 若有，则找出与该动画最近的一个退出滤镜动画，并将退出滤镜动画恢复为元素可见状态
     * @param params IAnimCompleteParams
     */
    private handleFilterAnimBeforeCurrent(params: IAnimCompleteParams) {
        const { animTarget, animModel } = params;
        const animList = animModel.elementModel?.animList;
        if (!animList?.length) return;
        const list = animList.filter((item) => {
            const {
                playOrder,
                props: { presetClass },
            } = item;
            return playOrder < animModel.playOrder && presetClass === presetClassTypes.EXIT;
        });
        if (!list.length) return;
        const lastExitAnimModel = list[list.length - 1];
        const lastExitAnimType = lastExitAnimModel.props.animType;
        const lastExitAnimFilterType = this.animFiltersManager.getAnimFilterType(lastExitAnimType);
        if (!lastExitAnimFilterType) return;
        this.animFiltersManager.handleAnimFiltersComplete(
            lastExitAnimFilterType,
            animTarget,
            (animModel?.props.presetClass as Action) || Action.ENTR,
            true,
        );
    }

    /**
     * 动画开始时，让入场动画元素可见
     * @param params IAnimStartParams
     */
    private makeEntrAnimVisibleOnStart(params: IAnimStartParams): void {
        const { animTarget, animModel } = params;
        if (animModel.props.presetClass !== presetClassTypes.ENTR) return;
        if (animTarget.alpha === 0) {
            animTarget.alpha = 1;
        }
        if (!animTarget.visible) {
            animTarget.visible = true;
        }
    }

    /**
     * 开发模式下检查新增动画是否已注册
     * @param params IAnimStartParams
     */
    private checkAnimInDevMode(params: IAnimStartParams): void {
        if (!__IS_DEV__) return;
        // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires
        const { animsWithE2E, animsWithoutE2E } = require('../../../../../../../../check-anim/check-anim-data');
        const animType = params.animModel?.props.animType;

        // 针对新增动画
        if (!animsWithoutE2E.includes(animType) && !animsWithE2E.includes(animType)) {
            const errMsg = i18n.t(`动画${animType}尚未支持，开发时需在check-anim-data.js文件的animsWithE2E中声明该动画类型`);
            import('@tencent/dui/lib/components/Modal').then((Modal) => {
                Modal.default.confirm({
                    content: errMsg,
                    cancelText: null,
                    closable: true,
                });
            });
            throw new Error(errMsg);
        }

        // 针对已上线，但缺少运行时E2E的动画
        if (animsWithoutE2E.includes(animType)) {
            console.warn(i18n.t(`该动画类型${animType}缺少运行时E2E，请尽快补齐`));
        }
    }
}
